import { atom, useAtom } from "jotai";
import { supabase } from "../lib/supabase";
import { useEffect, useCallback } from "react";

export type Rep = {
  id: string;
  full_name: string;
  territories_count: number;
  stores_count: number;
  agency_names: string;
};

const repsAtom = atom<Rep[]>([]);

async function buildRepData(rep: {
  id: string;
  full_name: string;
}): Promise<Rep> {
  // Get territories with agency names for this rep
  const { data: territories, error: territoriesError } = await supabase
    .from("territories")
    .select("id, agencies(name)")
    .eq("user_id", rep.id);

  if (territoriesError) {
    console.error("Error fetching territories:", territoriesError);
  }

  const territoriesCount = territories?.length ?? 0;

  // Get distinct agency names
  const agencyNames = [
    ...new Set(
      (territories ?? []).map((t: any) => t.agencies?.name).filter(Boolean),
    ),
  ].join(", ");

  // Get stores count via stores_territories
  const { count: storesCount, error: storesError } = await supabase
    .from("stores_territories")
    .select("store_id, territories!inner(user_id)", {
      count: "exact",
      head: true,
    })
    .eq("territories.user_id", rep.id);

  if (storesError) {
    console.error("Error fetching stores count:", storesError);
  }

  return {
    id: rep.id,
    full_name: rep.full_name,
    territories_count: territoriesCount,
    stores_count: storesCount ?? 0,
    agency_names: agencyNames,
  };
}

export function useReps() {
  const [reps, setReps] = useAtom(repsAtom);

  const fetchReps = useCallback(async () => {
    try {
      const { data: repsData, error: repsError } = await supabase
        .from("users")
        .select(
          `
          id,
          full_name,
          user_roles!inner(role)
        `,
        )
        .eq("user_roles.role", "rep")
        .is("deleted_at", null)
        .order("full_name", { ascending: true });

      if (repsError) throw repsError;
      if (!repsData) {
        setReps([]);
        return;
      }

      const repsWithCounts = await Promise.all(repsData.map(buildRepData));
      setReps(repsWithCounts);
    } catch (err) {
      console.error("Error fetching reps:", err);
    }
  }, [setReps]);

  useEffect(() => {
    fetchReps();
  }, [fetchReps]);

  return [reps, fetchReps] as const;
}

export async function fetchRepsForManager(): Promise<{
  data: Rep[];
  hasMore: boolean;
  totalCount: number;
}> {
  try {
    const { data: repsData, error: repsError } = await supabase
      .from("users")
      .select(
        `
        id,
        full_name,
        user_roles!inner(role)
      `,
      )
      .eq("user_roles.role", "rep")
      .is("deleted_at", null)
      .order("full_name", { ascending: true });

    if (repsError) throw repsError;
    if (!repsData) return { data: [], hasMore: false, totalCount: 0 };

    const repsWithCounts = await Promise.all(repsData.map(buildRepData));

    return {
      data: repsWithCounts,
      hasMore: false,
      totalCount: repsWithCounts.length,
    };
  } catch (err) {
    console.error("Error fetching reps:", err);
    throw err;
  }
}
